import { Formik, Form } from 'formik'
import { useEffect, useState } from 'react'

import type { ReactElement } from 'react'
import type { IAssetProperty } from '~/types/assets-management'

import PropertiesModalForm from './PropertiesModalForm'

import Button from '~/components/Buttons/Button'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { useAssetsTagsKeysQuery, useBatchTagsUpdateMutation } from '~/queries/assets'
import { getKeysOptions, getTagsArray } from '~/utils/asset'

const MODAL_ID = 'add-properties-modal'

interface FormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  selectedAssetsIds: string[]
}

export interface FormValues {
  properties: IAssetProperty[]
}

const initialValues: FormValues = {
  properties: [{ key: null, value: '' }],
}

const AddPropertiesModalForm = ({ onClose, onFailure, onSuccess, selectedAssetsIds }: FormProps): ReactElement => {
  const { data: tagsKeys, isLoading } = useAssetsTagsKeysQuery()

  const { mutateAsync: batchTagsUpdate } = useBatchTagsUpdateMutation()

  const [keysOptions, setKeysOptions] = useState(getKeysOptions(tagsKeys))

  const handleSubmit = async (values: FormValues) => {
    const { properties } = values

    try {
      const updateData = {
        assets_tags: selectedAssetsIds.reduce(
          (obj, key) => Object.assign(obj, { [key]: getTagsArray(properties) }),
          {}
        ),
      }

      await batchTagsUpdate(updateData)
      onSuccess()
    } catch {
      onFailure()
    }
  }

  useEffect(() => {
    if (tagsKeys) {
      setKeysOptions(getKeysOptions(tagsKeys))
    }
  }, [tagsKeys])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, values }) => (
        <Form>
          <ModalHeader>Update assets ({selectedAssetsIds.length})</ModalHeader>
          <ModalBody>
            {isLoading ? (
              <MwMLoader category="item" />
            ) : (
              <PropertiesModalForm formValues={values} modalId={MODAL_ID} selectOptions={keysOptions} />
            )}
          </ModalBody>
          <ModalFooter>
            <Button ghost onClick={onClose} type="button">
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              Save
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  )
}

interface Props {
  isOpen: boolean
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  selectedAssetsIds: string[]
}

const AddPropertiesModal = ({ isOpen, onClose, onFailure, onSuccess, selectedAssetsIds }: Props): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <AddPropertiesModalForm
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        selectedAssetsIds={selectedAssetsIds}
      />
    </Modal>
  )
}

export default AddPropertiesModal
